import React, { useMemo } from "react";
import { withTranslation } from "react-i18next";

import Breadcrumbs from "components/Common/Breadcrumb";
import { Container, Card, CardBody, Alert } from "reactstrap";
import { useParams, useLocation } from "react-router-dom";
import dayjs from "dayjs";
import { useGetPartnerQuery } from "services/partners/api";
import ShimmerLoading from "components/Common/Shimmer";
import link from "assets/icons/link.svg";
import Avatar from "components/Avatar/Avatar";

const PartnerInfo = (props) => {
  //meta title
  document.title = `${props.t("PartnersInfo")} | ${process.env.REACT_APP_CMS_TITLE}`;
  const { id } = useParams();
  const location = useLocation();
  const { data, isLoading, error } = useGetPartnerQuery(id);

  const searchParams = new URLSearchParams(location.search);
  const source = searchParams.get("source");
  const backUrl = useMemo(() => {
    if (source) {
      return `/partners?tab=${source}`;
    }
    return "/partners";
  }, [source]);

  function renderContentText(title, content) {
    return (
      <div className="d-flex gap-1 align-items-center text-xl text-gray-500">
        <div className="fw-semibold">{title}</div>
        <div className="fw-semibold">:</div>
        <div>{content}</div>
      </div>
    );
  }

  // Helper function to format array values
  const formatArrayToString = (array) => {
    if (!array || !Array.isArray(array)) return "-";
    return array.join(", ");
  };

  return (
    <div className="page-content" data-testid="partners-page">
      <Container fluid>
        <Breadcrumbs title={props.t("PartnersInfo")} breadcrumbItem={props.t("PartnersInfo")} back="กลับหน้าจัดการพาร์ทเนอร์ Affiliate" backUrl={backUrl} />

        <Card className="card-info mt-4 mb-4">
          {isLoading ? (
            <div className="text-center">
              <ShimmerLoading fullWidth={true} height={800} />
            </div>
          ) : error ? (
            <Alert color="danger">{error.message || "เกิดข้อผิดพลาดในการโหลดข้อมูล"}</Alert>
          ) : (
            <CardBody>
              <div className="partner-info d-flex flex-col gap-3">
                <h3 className="fw-semibold text-primary">ข้อมูลส่วนตัว</h3>
                <Avatar src={data.profilePicture} alt={`${data.firstName} ${data.lastName}`} size="lg" />
                <h3 className="fw-semibold text-4xl text-gray-700">
                  {data.firstName} {data.lastName}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {renderContentText("วันเกิด", data.birthdate ? dayjs(data.birthdate).format("D MMMM YYYY") : "-")}
                  {renderContentText("เบอร์โทรศัพท์", data.phoneNo || "-")}
                  {renderContentText("อีเมลผู้ใช้", data.email || "-")}
                  {renderContentText(
                    "ลิงค์โซเซี่ยลมีเดีย",
                    data.socialMedia && data.socialMedia.length > 0 && data.socialMedia[0].url ? (
                      <a
                        href={data.socialMedia[0].url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="d-inline-flex align-items-center gap-1 text-primary text-decoration-none"
                      >
                        ดูลิงค์โซเชียลมีเดีย
                        <img
                          src={link}
                          alt="external link"
                          className="w-4 h-4 ml-1"
                        />
                      </a>
                    ) : "-"
                  )}
                  {renderContentText("วันที่สมัครสมาชิก", data.createdAt ? dayjs(data.createdAt).format("YYYY-MM-DD") : "-")}
                  {renderContentText("ยอมรับนโยบายความเป็นส่วนตัว", data.pdpaAccept ? "ยอมรับแล้ว" : "ยังไม่ยอมรับ")}
                </div>
                {renderContentText(
                  "ที่อยู่พาร์ทเนอร์",
                  data.addresses && data.addresses.length > 0 ? (
                    <div className="d-flex gap-2">
                      <div>{data.addresses[0].addressLine1}</div>
                      <div>
                        <span className="text-gray-500">ตำบล</span>
                        {data.addresses[0].subDistrict}
                      </div>
                      <div>
                        <span className="text-gray-500">อำเภอ</span>
                        {data.addresses[0].district}
                      </div>
                      <div>
                        <span className="text-gray-500">จังหวัด</span>
                        {data.addresses[0].province}
                      </div>
                      <div>{data.addresses[0].postalCode}</div>
                    </div>
                  ) : (
                    "-"
                  )
                )}
              </div>

              {data.partnerType === "Company" && (
                <>
                  <hr className="my-8" />
                  <div className="company-info d-flex flex-col gap-3">
                    <h3 className="fw-semibold text-primary">ข้อมูลบริษัท</h3>
                    <div className="d-flex gap-20">
                      {renderContentText("ชื่อบริษัท", data.companyName || "-")}
                      {renderContentText("หมวดหมู่ธุรกิจ", data.companyIndustry || "-")}
                    </div>
                    {renderContentText("ช่องทางการตลาดที่ใช้งาน", formatArrayToString(data.marketingChannel || "-"))}
                    {renderContentText("เลขประจำตัวผู้เสียภาษี", data.taxNo || "-")}
                  </div>
                </>
              )}

              {data.bankAccounts.length > 0 && (
                <>
                  <hr className="my-8" />
                  <div className="bank-info d-flex flex-col gap-3">
                    <h3 className="fw-semibold text-primary">ข้อมูลผู้รับเงินและบัญชี</h3>
                    <div className="d-flex gap-20">
                      {renderContentText("เลขบัตรประจำตัวประชาชน / เลขที่จดทะเบียนบริษัท", data.taxNo || "-")}
                      {renderContentText("ชื่อเจ้าของบัญชี", `${data.firstName} ${data.lastName}`)}
                    </div>
                    {renderContentText("เลขที่บัญชี", data.bankAccounts[0].accountNumber || "-")}
                    {renderContentText("ธนาคารที่ใช้สมัครบัญชีี", data.bankAccounts[0].bankName || "-")}
                    {renderContentText("รหัสสาขาที่ทำการเปิดบัญชีี", data.bankAccounts[0].branchName || "-")}
                  </div>
                </>
              )}
            </CardBody>
          )}
        </Card>
      </Container>
    </div>
  );
};

export default withTranslation()(PartnerInfo);
